/* eslint-disable eslint-comments/no-unlimited-disable */
/* eslint-disable */

import { cleanUndefined } from '@/src/utils';
import { api } from 'dicomweb-client-typed';
import { supabase, session, DicomArchiveConfig } from '@/src/supabase';
import { computed, ref, watch } from 'vue';

export interface FetchStudyOptions {
  studyInstanceUID: string;
}

export interface FetchSeriesOptions extends FetchStudyOptions {
  seriesInstanceUID: string;
}

export interface FetchInstanceOptions extends FetchSeriesOptions {
  sopInstanceUID: string;
}

export interface StoreInstancesOptions {
  datasets: ArrayBuffer[];
  studyInstanceUID?: string;
}

export interface FileProgress {
  index: number;
  total: number;
  success: boolean;
  error?: string;
}

const tags = {
  PatientID: '00100020',
  PatientName: '00100010',
  PatientBirthDate: '00100030',
  PatientSex: '00100040',

  StudyID: '00200010',
  StudyInstanceUID: '0020000D',
  StudyName: '00100010',
  StudyDate: '00080020',
  StudyTime: '00080030',
  AccessionNumber: '00080050',
  StudyDescription: '00081030',
  Manufacturer: '00080070',
  InstitutionName: '00080080',
  Modality: '00080060',

  WindowCenter: '00281050',
  WindowWidth: '00281051',

  SeriesInstanceUID: '0020000E',
  SeriesNumber: '00200011',
  SeriesDescription: '0008103E',

  ImageComments: '00204000',

  SOPInstanceUID: '00080018',
  SOPClassUID: '00080016',
  InstanceNumber: '00200013',
  NumberOfFrames: '00280008',
  Rows: '00280010',
  Columns: '00280011',
  SamplesPerPixel: '00280002',
  PhotometricInterpretation: '00280004',
  BitsAllocated: '00280100',
  PixelRepresentation: '00280103',
  RescaleIntercept: '00281052',
  RescaleSlope: '00281053',
  RescaleType: '00281054',
  ImageType: '00080008',
  AcquisitionTime: '00080032',
  SequenceName: '00180024',
  SliceThickness: '00180050',
  ProtocolName: '00181030',
  TriggerTime: '00181060',
  CardiacNumberofImages: '00181090',
  FrameofReferenceUID: '00200052',
  SliceLocation: '00201041',
  ImagePositionPatient: '00200032',
  ImageOrientationPatient: '00200037',
  PixelSpacing: '00280030',
};

const perFrameTags = {
  // Shared and Per-frame Functional Groups Sequences
  SharedFunctionalGroupsSequence: '52009229',
  PerFrameFunctionalGroupsSequence: '52009230',

  // MR Echo Sequence
  MREchoSequence: '00189114',
  EchoNumbers: '00180086',
  EffectiveEchoTime: '00189082',

  // Cardiac Trigger Sequence
  CardiacTriggerSequence: '00189118',
  TriggerDelayTime: '00209153',
  NominalPercentageOfCardiacPhase: '00209241',
  RRIntervalTimeNominal: '00209251',

  // MR Averages Sequence
  MRAveragesSequence: '00189119',
  NumberOfAverages: '00180083',

  // MR Image Frame Type Sequence
  MRImageFrameTypeSequence: '00189226',
  FrameType: '00089007',
  PixelPresentation: '00089205',
  VolumetricProperties: '00089206',
  VolumeBasedCalculationTechnique: '00089207',
  ComplexImageComponent: '00089208',
  AcquisitionContrast: '00089209',

  // Frame Content Sequence
  FrameContentSequence: '00209111',
  FrameAcquisitionDatetime: '00189074',
  FrameReferenceDatetime: '00189151',
  FrameAcquisitionDuration: '00189220',
  StackID: '00209056',
  InStackPositionNumber: '00209057',
  TemporalPositionIndex: '00209128',
  FrameAcquisitionNumber: '00209156',
  DimensionIndexValues: '00209157',

  // Plane Position and Orientation Sequences
  PlanePositionSequence: '00209113',
  ImagePositionPatient: '00200032',
  PlaneOrientationSequence: '00209116',
  ImageOrientationPatient: '00200037',

  // Pixel Measures and VOI LUT Sequences
  PixelMeasuresSequence: '00289110',
  SliceThickness: '00180050',
  SpacingBetweenSlices: '00180088',
  PixelSpacing: '00280030',
  WindowCenter: '00281050',
  WindowWidth: '00281051',
  PixelValueTransformationSequence: '00289145',
  RescaleIntercept: '00281052',
  RescaleSlope: '00281053',
  RescaleType: '00281054',
  FrameVOILUTSequence: '00289132',
};

// Enhanced multiframe SOP Class UIDs
export const ENHANCED_MULTIFRAME_SOP_CLASSES = [
  '1.2.840.10008.5.1.4.1.1.4.1', // Enhanced MR Image Storage
  '1.2.840.10008.5.1.4.1.1.2.1', // Enhanced CT Image Storage
];

// Per-frame sequence types
export interface MREchoItem {
  EchoNumbers: string;
  EffectiveEchoTime: string;
}

export interface CardiacTriggerItem {
  TriggerDelayTime: string;
  NominalPercentageOfCardiacPhase: string;
  RRIntervalTimeNominal: string;
}

export interface MRAveragesItem {
  NumberOfAverages: string;
}

export interface MRImageFrameTypeItem {
  FrameType: string;
  PixelPresentation: string;
  VolumetricProperties: string;
  VolumeBasedCalculationTechnique: string;
  ComplexImageComponent: string;
  AcquisitionContrast: string;
}

export interface FrameContentItem {
  FrameAcquisitionDatetime: string;
  FrameReferenceDatetime: string;
  FrameAcquisitionDuration: string;
  StackID: string;
  InStackPositionNumber: string;
  TemporalPositionIndex: string;
  FrameAcquisitionNumber: string;
  DimensionIndexValues: string[];
}

export interface PlanePositionItem {
  ImagePositionPatient: string;
}

export interface PlaneOrientationItem {
  ImageOrientationPatient: string;
}

export interface PixelMeasuresItem {
  SliceThickness: string;
  SpacingBetweenSlices: string;
  PixelSpacing: string;
}

export interface FrameVOILUTItem {
  WindowCenter: string;
  WindowWidth: string;
}

export interface PixelValueTransformationItem {
  RescaleIntercept: string;
  RescaleSlope: string;
  RescaleType: string;
}

export interface PerFrameFunctionalGroupsItem {
  MREchoSequence?: MREchoItem[];
  CardiacTriggerSequence?: CardiacTriggerItem[];
  MRAveragesSequence?: MRAveragesItem[];
  MRImageFrameTypeSequence?: MRImageFrameTypeItem[];
  FrameContentSequence?: FrameContentItem[];
  PlanePositionSequence?: PlanePositionItem[];
  PlaneOrientationSequence?: PlaneOrientationItem[];
  PixelMeasuresSequence?: PixelMeasuresItem[];
  FrameVOILUTSequence?: FrameVOILUTItem[];
  PixelValueTransformationSequence?: PixelValueTransformationItem[];
}

export type Instance = typeof tags;

export interface ParsedInstance {
  instance: Instance;
  perFrameGroups?: PerFrameFunctionalGroupsItem[];
}

function parseTag(value: any) {
  const values = value?.Value;
  if (!values || values.length === 0) return undefined;

  if (values.length === 1) {
    const v = values[0];
    return v?.Alphabetic || v;
  }
  return values;
}

function parseInstance(instance: any): Instance {
  const withNamedTags = Object.entries(tags).reduce(
    (info, [key, tag]) => ({ ...info, [key]: parseTag(instance[tag]) }),
    {} as Instance
  );
  return cleanUndefined(withNamedTags) as Instance;
}

/**
 * Check if a DICOM instance is an enhanced multiframe object based on metadata
 */
function isEnhancedMultiframe(metadata: any): boolean {
  const sopClassUID = parseTag(metadata[tags.SOPClassUID]);

  if (!sopClassUID) return false;

  // Check if SOP Class UID matches enhanced multiframe types
  const isEnhancedSOPClass =
    ENHANCED_MULTIFRAME_SOP_CLASSES.includes(sopClassUID);

  return isEnhancedSOPClass;
}

function getSeqItems(group: any, seqTag: string): any[] | undefined {
  return group?.[seqTag]?.Value as any[] | undefined;
}

function mapSeq<T>(
  group: any,
  seqTag: string,
  mapItem: (item: any) => T
): T[] | undefined {
  const items = getSeqItems(group, seqTag);
  return items?.map(mapItem);
}

function parsePerFrameGroup(
  group: any
): PerFrameFunctionalGroupsItem | undefined {
  if (!group) return undefined;

  return {
    MREchoSequence: mapSeq(group, perFrameTags.MREchoSequence, (item) => ({
      EchoNumbers: parseTag(item[perFrameTags.EchoNumbers]),
      EffectiveEchoTime: parseTag(item[perFrameTags.EffectiveEchoTime]),
    })),
    CardiacTriggerSequence: mapSeq(
      group,
      perFrameTags.CardiacTriggerSequence,
      (item) => ({
        TriggerDelayTime: parseTag(item[perFrameTags.TriggerDelayTime]),
        NominalPercentageOfCardiacPhase: parseTag(
          item[perFrameTags.NominalPercentageOfCardiacPhase]
        ),
        RRIntervalTimeNominal: parseTag(
          item[perFrameTags.RRIntervalTimeNominal]
        ),
      })
    ),
    MRAveragesSequence: mapSeq(
      group,
      perFrameTags.MRAveragesSequence,
      (item) => ({
        NumberOfAverages: parseTag(item[perFrameTags.NumberOfAverages]),
      })
    ),
    MRImageFrameTypeSequence: mapSeq(
      group,
      perFrameTags.MRImageFrameTypeSequence,
      (item) => ({
        FrameType: parseTag(item[perFrameTags.FrameType]),
        PixelPresentation: parseTag(item[perFrameTags.PixelPresentation]),
        VolumetricProperties: parseTag(item[perFrameTags.VolumetricProperties]),
        VolumeBasedCalculationTechnique: parseTag(
          item[perFrameTags.VolumeBasedCalculationTechnique]
        ),
        ComplexImageComponent: parseTag(
          item[perFrameTags.ComplexImageComponent]
        ),
        AcquisitionContrast: parseTag(item[perFrameTags.AcquisitionContrast]),
      })
    ),
    FrameContentSequence: mapSeq(
      group,
      perFrameTags.FrameContentSequence,
      (item) => ({
        FrameAcquisitionDatetime: parseTag(
          item[perFrameTags.FrameAcquisitionDatetime]
        ),
        FrameReferenceDatetime: parseTag(
          item[perFrameTags.FrameReferenceDatetime]
        ),
        FrameAcquisitionDuration: parseTag(
          item[perFrameTags.FrameAcquisitionDuration]
        ),
        StackID: parseTag(item[perFrameTags.StackID]),
        InStackPositionNumber: parseTag(
          item[perFrameTags.InStackPositionNumber]
        ),
        TemporalPositionIndex: parseTag(
          item[perFrameTags.TemporalPositionIndex]
        ),
        FrameAcquisitionNumber: parseTag(
          item[perFrameTags.FrameAcquisitionNumber]
        ),
        DimensionIndexValues: parseTag(item[perFrameTags.DimensionIndexValues]),
      })
    ),
    PlanePositionSequence: mapSeq(
      group,
      perFrameTags.PlanePositionSequence,
      (item) => ({
        ImagePositionPatient: parseTag(item[perFrameTags.ImagePositionPatient]),
      })
    ),
    PlaneOrientationSequence: mapSeq(
      group,
      perFrameTags.PlaneOrientationSequence,
      (item) => ({
        ImageOrientationPatient: parseTag(
          item[perFrameTags.ImageOrientationPatient]
        ),
      })
    ),
    PixelMeasuresSequence: mapSeq(
      group,
      perFrameTags.PixelMeasuresSequence,
      (item) => ({
        SliceThickness: parseTag(item[perFrameTags.SliceThickness]),
        SpacingBetweenSlices: parseTag(item[perFrameTags.SpacingBetweenSlices]),
        PixelSpacing: parseTag(item[perFrameTags.PixelSpacing]),
      })
    ),
    FrameVOILUTSequence: mapSeq(
      group,
      perFrameTags.FrameVOILUTSequence,
      (item) => ({
        WindowCenter: parseTag(item[perFrameTags.WindowCenter]),
        WindowWidth: parseTag(item[perFrameTags.WindowWidth]),
      })
    ),
    PixelValueTransformationSequence: mapSeq(
      group,
      perFrameTags.PixelValueTransformationSequence,
      (item) => ({
        RescaleIntercept: parseTag(item[perFrameTags.RescaleIntercept]),
        RescaleSlope: parseTag(item[perFrameTags.RescaleSlope]),
        RescaleType: parseTag(item[perFrameTags.RescaleType]),
      })
    ),
  };
}

export function parsePerFrameData(
  instance: any
): PerFrameFunctionalGroupsItem[] | undefined {
  const seqElem = instance?.[perFrameTags.PerFrameFunctionalGroupsSequence];
  const frames = seqElem?.Value as any[] | undefined;
  if (!frames?.length) return undefined;

  const perFrameGroups = frames
    .map((group: any) => parsePerFrameGroup(group))
    .filter((g): g is PerFrameFunctionalGroupsItem => g !== undefined);

  return perFrameGroups.length ? perFrameGroups : undefined;
}

// Create unique file names so loader utils work
let fileCounter = 0;
function toFile(instance: ArrayBuffer) {
  fileCounter++;
  return new File([new Blob([instance])], `dicom-web.${fileCounter}.dcm`);
}

// Orthanc JWT cache
const orthancJwts = ref<Record<string, string>>({});

export async function orthancJwt(id: string) {
  if (!session.value) return null;
  if (orthancJwts.value[id]) return orthancJwts.value[id];

  const { data: jwt, error } = await supabase.rpc('make_orthanc_jwt', { id });
  if (error) {
    console.error(error.message);
    return null;
  }
  if (!jwt) {
    console.error('No JWT returned for Orthanc ID:', id);
    return null;
  }
  orthancJwts.value[id] = jwt;

  return jwt;
}

// Clear cached JWTs when the session changes
watch(session, async () => {
  orthancJwts.value = {};
});

async function makeClient(dicomArchiveConfig: DicomArchiveConfig) {
  return new api.DICOMwebClient({
    url: dicomArchiveConfig.url,
    headers: {
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      token: await orthancJwt(dicomArchiveConfig.id),
    },
  });
}

// ToDo: handle non dicom files
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (event) {
      const arrayBuffer = event.target?.result as ArrayBuffer;
      resolve(arrayBuffer);
    };

    reader.onerror = function (event) {
      reject(
        new Error(`Error reading file ${file.name}: ${event.target?.error}`)
      );
    };

    reader.readAsArrayBuffer(file);
  });
}

const NON_DICOM_FILE_NAMES = ['.DS_Store', 'Thumbs.db'];
function readFilesAsArrayBuffers(
  files: FileList | File[]
): Promise<ArrayBuffer[]> {
  const fileListArray = Array.from(files);
  return Promise.all(
    fileListArray
      .filter((item) => !NON_DICOM_FILE_NAMES.includes(item.name))
      .map((file) => readFileAsArrayBuffer(file))
  );
}

export async function searchForStudies(dicomArchiveConfig: DicomArchiveConfig) {
  const client = await makeClient(dicomArchiveConfig);
  const instances = await client.searchForStudies();
  return instances.map(parseInstance);
}

export async function retrieveStudyMetadata(
  dicomArchiveConfig: DicomArchiveConfig,
  options: FetchStudyOptions
) {
  const client = await makeClient(dicomArchiveConfig);
  const instances = await client.searchForSeries(options);
  return instances.map(parseInstance);
}

export async function retrieveSeriesMetadata(
  dicomArchiveConfig: DicomArchiveConfig,
  options: FetchSeriesOptions
) {
  const client = await makeClient(dicomArchiveConfig);
  const instances = await client.retrieveSeriesMetadata(options);

  // Debug: run full Orthanc API dump in a popup (non-blocking)
  import('./orthanc-debug-dump')
    .then(({ runOrthancDebugDump }) => {
      console.log('Running Orthanc debug dump - series metadata');
      runOrthancDebugDump(dicomArchiveConfig, options.studyInstanceUID);
    })
    .catch((e) => console.warn('Debug dump failed to load:', e));
  // Debug: run full DICOMweb API dump in a popup (non-blocking)
  // @ts-ignore dynamic import for debug-only module
  // import('./dicom-web-debug-dump').then(({ runDICOMwebDebugDump }) => {
  //   runDICOMwebDebugDump(orthancConfig, options.studyInstanceUID);
  // }).catch((e) => console.warn('DICOMweb debug dump failed to load:', e));

  return instances.map(parseInstance);
}

export async function retrieveSeriesMetadataEnhanced(
  dicomArchiveConfig: DicomArchiveConfig,
  options: FetchSeriesOptions
) {
  const client = await makeClient(dicomArchiveConfig);
  const instances = await client.retrieveSeriesMetadata(options);

  // Debug: run full Orthanc API dump in a popup (non-blocking)
  import('./orthanc-debug-dump')
    .then(({ runOrthancDebugDump }) => {
      console.log('Running Orthanc debug dump');
      runOrthancDebugDump(dicomArchiveConfig, options.studyInstanceUID);
    })
    .catch((e) => console.warn('Debug dump failed to load:', e));
  // Debug: run full DICOMweb API dump in a popup (non-blocking)
  // @ts-ignore dynamic import for debug-only module
  // import('./dicom-web-debug-dump').then(({ runDICOMwebDebugDump }) => {
  //   runDICOMwebDebugDump(orthancConfig, options.studyInstanceUID);
  // }).catch((e) => console.warn('DICOMweb debug dump failed to load:', e));

  return instances.map((instance) => ({
    instance: parseInstance(instance),
    perFrameGroups: parsePerFrameData(instance),
  }));
}

export async function retrieveInstanceMetadata(
  dicomArchiveConfig: DicomArchiveConfig,
  options: FetchInstanceOptions
) {
  const client = await makeClient(dicomArchiveConfig);
  const instance = await client.retrieveInstanceMetadata(options);
  return parseInstance(instance);
}

export async function retrieveInstanceMetadataEnhanced(
  dicomArchiveConfig: DicomArchiveConfig,
  options: FetchInstanceOptions
) {
  const client = await makeClient(dicomArchiveConfig);
  const instance = await client.retrieveInstanceMetadata(options);
  return {
    instance: parseInstance(instance),
    perFrameGroups: parsePerFrameData(instance),
  };
}

export async function fetchSeries(
  dicomArchiveConfig: DicomArchiveConfig,
  options: FetchSeriesOptions,
  progressCallback: (n: ProgressEvent) => void
): Promise<File[]> {
  const client = await makeClient(dicomArchiveConfig);
  const series = (await client.retrieveSeries({
    ...options,
    progressCallback,
  })) as ArrayBuffer[];
  return series.map(toFile);
}

export async function fetchInstance(
  dicomArchiveConfig: DicomArchiveConfig,
  instance: FetchInstanceOptions
) {
  const client = await makeClient(dicomArchiveConfig);
  const image = (await client.retrieveInstance({
    ...instance,
  })) as ArrayBuffer;

  return toFile(image);
}

// Returns an thumbnail image of a DICOM instance, for enhanced mr and ct objects returns the thumbnail of the first frame
// Frame number for enhanced objetcs can be given by add a postfix to the sopInstanceUID, e.g. "-1" for the first frame, "-2" for the second frame, etc.
// If checkEnhancedObject is true, the function will check if the instance is an enhanced multiframe object and return the thumbnail of the first frame, if not given
// Otherwise the call assumes single frame objects when no postfix is given
export async function fetchInstanceThumbnail(
  dicomArchiveConfig: DicomArchiveConfig,
  instance: FetchInstanceOptions,
  checkEnhancedObject: boolean = false
) {
  const client = await makeClient(dicomArchiveConfig);

  let isEnhancedObject = true;

  let frameNumber = instance.sopInstanceUID.split('-')[1];
  if (!frameNumber) frameNumber = '1';
  instance.sopInstanceUID = instance.sopInstanceUID.split('-')[0];

  try {
    // Check if this is an enhanced multiframe DICOM object
    if (isEnhancedObject) {
      const data = await client.retrieveInstanceFramesRendered({
        ...instance,
        frameNumbers: frameNumber,
        mediaTypes: [{ mediaType: 'image/jpeg' }],
      });
      const buf = Array.isArray(data) ? data[0] : data;
      if (!buf || (buf as ArrayBuffer).byteLength === 0) return null;
      const blob = new Blob([buf as ArrayBuffer], { type: 'image/jpeg' });
      return URL.createObjectURL(blob);
    } else {
      // Use the standard rendered instance retrieval for non-enhanced objects
      const thumbnail = await client.retrieveInstanceRendered({
        ...instance,
        mediaTypes: [{ mediaType: 'image/jpeg' }],
      });
      const arrayBufferView = new Uint8Array(thumbnail);
      const blob = new Blob([arrayBufferView], { type: 'image/jpeg' });
      return URL.createObjectURL(blob);
    }
  } catch (error) {
    console.warn('Thumbnail retrieval failed', error);
    return null;
  }
}

export async function fetchInstanceFrameRaw16(
  dicomArchiveConfig: DicomArchiveConfig,
  instance: FetchInstanceOptions
): Promise<Uint16Array | undefined> {
  const uid = instance?.sopInstanceUID;
  if (!uid) return undefined;

  const [sopInstanceUID, framePart] = uid.split('-');
  let frameNumbers = framePart || '0';

  const client = await makeClient(dicomArchiveConfig);
  const parts = await client.retrieveInstanceFrames({
    ...instance,
    sopInstanceUID,
    frameNumbers,
  });

  const buf = parts?.[0] as ArrayBuffer | undefined;
  return buf ? new Uint16Array(buf) : undefined;
}

export async function storeInstances(
  dicomArchiveConfig: DicomArchiveConfig,
  dicomFiles: FileList,
  progressCallback?: (progress: FileProgress) => void
) {
  const client = await makeClient(dicomArchiveConfig);
  try {
    const files = Array.from(dicomFiles);
    const zips = files.filter((f) => f.name.toLowerCase().endsWith('.zip'));
    const potentialDicoms = files.filter(
      (f) =>
        !f.name.toLowerCase().endsWith('.zip') &&
        (f.name.toLowerCase().endsWith('.dcm') || !f.name.includes('.'))
    );

    const dicomBuffers = await readFilesAsArrayBuffers(potentialDicoms);
    const total = zips.length + dicomBuffers.length;
    let processed = 0;

    // Upload ZIPs
    for (const zip of zips) {
      processed++;
      try {
        const url =
          dicomArchiveConfig.url.replace(/\/dicom-web\/?$/, '') + '/instances';
        const token = await orthancJwt(dicomArchiveConfig.id);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            token: token || '',
            'Content-Type': 'application/zip',
          },
          body: zip,
        });

        if (!response.ok) {
          throw new Error(`Zip upload failed: ${response.statusText}`);
        }

        progressCallback?.({
          index: processed,
          total,
          success: true,
        });
      } catch (error) {
        console.error('Error storing ZIP:', error);
        progressCallback?.({
          index: processed,
          total,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    for (let i = 0; i < dicomBuffers.length; i++) {
      processed++;
      try {
        const storeOptions: StoreInstancesOptions = {
          datasets: [dicomBuffers[i]],
        };
        await client.storeInstances(storeOptions);

        progressCallback?.({
          index: processed,
          total,
          success: true,
        });
      } catch (error) {
        console.error('Error storing DICOM instance:', error);
        progressCallback?.({
          index: processed,
          total,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }
  } catch (error) {
    console.error('Error reading files:', error);
    throw error;
  }
}

const LEVELS = ['series', 'studies'] as const;
type DicomLevel = (typeof LEVELS)[number];

// takes a url like http://localhost:3000/dicom-web/studies/someid/series/anotherid
// returns { host: 'http://localhost:3000/dicom-web', studies: 'someid', series: 'anotherid' }
export function parseUrl(deepDicomWebUrl: string) {
  // remove trailing slash
  const sansSlash = deepDicomWebUrl.replace(/\/$/, '');

  let paths = sansSlash.split('/');
  const parentIDs = LEVELS.reduce((idObj, dicomLevel) => {
    const [urlLevel, dicomID] = paths.slice(-2);
    if (urlLevel === dicomLevel) {
      paths = paths.slice(0, -2);
      return { [dicomLevel]: dicomID, ...idObj };
    }
    return idObj;
  }, {} as Partial<Record<DicomLevel, string>>);

  const pathsToSlice = Object.keys(parentIDs).length * 2;
  const allPaths = sansSlash.split('/');
  const host = allPaths.slice(0, allPaths.length - pathsToSlice).join('/');

  return { host, ...parentIDs };
}
